/**
 * IM 提示音 — HTMLAudioElement + 可选预置 / 自定义铃声
 *
 * 预置：/sounds/{ding|dingdong|tritone|pop|message}.wav（随前端部署，公开静态，URL 稳定）
 * 自定义：用户上传到 OSS 私有桶，play 时用后端 /me/settings 下发的 7 天签名 URL
 *   （new Audio 不带 JWT，故不走后端代理；桶保持私有，靠长有效期签名 URL）
 *
 * 偏好权威存后端 user.settings_json.im_sound（跨设备同步）；
 * 本地 localStorage 仅作快速兜底缓存（刷新 / 离线不闪、不静默）。
 * App 启动 / Settings 操作后调 configure() 注入最新偏好。
 *
 * 规则：1s debounce / 自己发不响 / 静音会话不响 / @我连播 2 次跨静音 / preset='none' 不响
 */

type EventKey = 'message' | 'mention' | 'approval' | 'alert';

export interface ImSoundPrefs {
  enabled?: boolean;
  volume?: number;
  preset?: string;            // ding | dingdong | tritone | pop | message | custom | none
  customUrl?: string | null;
}

const PRESET_FILES = ['message', 'ding', 'dingdong', 'tritone', 'pop'];
const LS = {
  enabled: 'im_sound_enabled',
  volume: 'im_sound_volume',
  preset: 'im_sound_preset',
  customUrl: 'im_sound_custom_url',
};

const DEBUG = true;
function log(...args: any[]) { if (DEBUG) console.log('[sound]', ...args); }
function clamp01(n: number) { return Math.max(0, Math.min(1, isNaN(n) ? 0.6 : n)); }
function safeSet(k: string, v: string) { try { localStorage.setItem(k, v); } catch { /* ignore */ } }

class NotificationSound {
  private audio: HTMLAudioElement | null = null;
  private mentionAudio: HTMLAudioElement | null = null;  // 第二个实例支持连播
  private lastPlayTime = 0;
  private debounceMs = 1000;
  private userEnabled = true;
  private volume = 0.6;
  private preset = 'ding';
  private customUrl: string | null = null;
  private curSrc = '';
  private unlocked = false;
  private pendingUnlock = false;

  constructor() {
    if (typeof window === 'undefined') return;
    // localStorage 兜底（上次同步的偏好，刷新/离线立即可用）
    const e = localStorage.getItem(LS.enabled);
    if (e !== null) this.userEnabled = e === 'true';
    const v = localStorage.getItem(LS.volume);
    if (v !== null) { const n = parseFloat(v); if (!isNaN(n)) this.volume = clamp01(n); }
    const p = localStorage.getItem(LS.preset);
    if (p) this.preset = p;
    this.customUrl = localStorage.getItem(LS.customUrl) || null;
    this.rebuild();
  }

  /** 注入后端权威偏好（App 启动 / Settings 操作后调），并缓存到 localStorage */
  configure(prefs: ImSoundPrefs) {
    if (prefs.enabled !== undefined) { this.userEnabled = !!prefs.enabled; safeSet(LS.enabled, String(this.userEnabled)); }
    if (prefs.volume !== undefined) { this.volume = clamp01(Number(prefs.volume)); safeSet(LS.volume, String(this.volume)); }
    if (prefs.preset !== undefined) { this.preset = prefs.preset || 'ding'; safeSet(LS.preset, this.preset); }
    if (prefs.customUrl !== undefined) { this.customUrl = prefs.customUrl || null; safeSet(LS.customUrl, this.customUrl ?? ''); }
    this.rebuild();
  }

  private resolveSrc(): string {
    if (this.preset === 'none') return '';
    if (this.preset === 'custom') return this.customUrl || '/sounds/ding.wav';
    const p = PRESET_FILES.includes(this.preset) ? this.preset : 'ding';
    return `/sounds/${p}.wav`;
  }

  /** src 变化时重建 Audio 实例；src 不变只更新音量 */
  private rebuild() {
    const src = this.resolveSrc();
    if (src === this.curSrc) {
      if (this.audio) this.audio.volume = this.volume;
      if (this.mentionAudio) this.mentionAudio.volume = this.volume;
      return;
    }
    this.curSrc = src;
    this.unlocked = false;
    if (!src) { this.audio = null; this.mentionAudio = null; return; }
    this.audio = new Audio(src);
    this.audio.volume = this.volume;
    this.audio.preload = 'auto';
    this.mentionAudio = new Audio(src);
    this.mentionAudio.volume = this.volume;
    this.mentionAudio.preload = 'auto';
    log('audio rebuilt: src=', src, 'vol=', this.volume);
    if (this.pendingUnlock) this.unlock();
  }

  /** 首次用户 gesture 内调，解锁浏览器自动播放策略 */
  unlock() {
    this.pendingUnlock = true;
    if (this.unlocked || !this.audio) return;
    const a = this.audio;
    a.muted = true;  // 静音播一次解锁，不让用户听到
    a.play().then(() => {
      a.pause(); a.currentTime = 0; a.muted = false;
      this.unlocked = true;
      log('unlocked OK');
    }).catch((err) => log('unlock blocked (need next gesture):', err?.message));
  }

  /** 主入口：业务侧调用 */
  play(event: EventKey = 'message', opts: {
    fromUserId?: number | string | null;
    currentUserId?: number | string | null;
    isMuted?: boolean;
    isMentioned?: boolean;
    force?: boolean;
  } = {}) {
    if (this.preset === 'none') { log('skip: preset none'); return; }
    if (opts.force) { this.actuallyPlay(event); return; }
    if (!this.userEnabled) { log('skip: disabled'); return; }
    if (opts.fromUserId != null && opts.currentUserId != null
        && Number(opts.fromUserId) === Number(opts.currentUserId)) {
      log('skip: own message'); return;
    }
    const finalEvent: EventKey = opts.isMentioned ? 'mention' : event;
    if (opts.isMuted && finalEvent !== 'mention') { log('skip: muted (non-mention)'); return; }
    const now = Date.now();
    if (now - this.lastPlayTime < this.debounceMs) { log('skip: debounced'); return; }
    this.lastPlayTime = now;
    this.actuallyPlay(finalEvent);
  }

  private actuallyPlay(event: EventKey) {
    if (!this.audio) return;
    if (event === 'mention' && this.mentionAudio) {
      // @我 连播 2 次（间隔 200ms）
      this.audio.currentTime = 0;
      this.audio.play().then(() => log('played (mention 1/2)')).catch((e) => console.warn('[sound] blocked:', e?.message));
      const second = this.mentionAudio;
      setTimeout(() => {
        second.currentTime = 0;
        second.play().then(() => log('played (mention 2/2)')).catch((e) => console.warn('[sound] blocked:', e?.message));
      }, 200);
    } else {
      this.audio.currentTime = 0;
      this.audio.play().then(() => log('played', event)).catch((e) => console.warn('[sound] blocked:', e?.message));
    }
  }

  // 本地乐观设置（Settings 同时 PUT 后端）
  setEnabled(enabled: boolean) { this.userEnabled = enabled; safeSet(LS.enabled, String(enabled)); }
  isEnabled() { return this.userEnabled; }

  setVolume(v: number) {
    this.volume = clamp01(v);
    if (this.audio) this.audio.volume = this.volume;
    if (this.mentionAudio) this.mentionAudio.volume = this.volume;
    safeSet(LS.volume, String(this.volume));
  }
  getVolume() { return this.volume; }

  setPreset(preset: string, customUrl?: string | null) {
    this.preset = preset || 'ding';
    safeSet(LS.preset, this.preset);
    if (customUrl !== undefined) { this.customUrl = customUrl || null; safeSet(LS.customUrl, this.customUrl ?? ''); }
    this.rebuild();
  }
  getPreset() { return this.preset; }
  getCustomUrl() { return this.customUrl; }

  /** 试听当前选中铃声（跳过 debounce / enabled / none 限制） */
  preview(_event?: EventKey) {
    if (this.audio) {
      this.audio.currentTime = 0;
      this.audio.play().catch((e) => console.warn('[sound] preview blocked:', e?.message));
    } else if (this.preset === 'none') {
      this.previewSrc('/sounds/ding.wav');  // none 无 audio，给个反馈音
    }
  }

  /** 试听任意 src（Settings 逐个预置 / 上传前预览，不改当前偏好） */
  previewSrc(src: string, vol?: number) {
    try {
      const a = new Audio(src);
      a.volume = vol !== undefined ? clamp01(vol) : this.volume;
      a.play().catch((e) => console.warn('[sound] previewSrc blocked:', e?.message));
    } catch { /* ignore */ }
  }
}

export const notificationSound = new NotificationSound();
